import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { DEPARTMENT_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS } from '../constants';
import { getAssignedId } from '../types';
import type {
  CreateProjectPayload,
  Project,
  ProjectPriority,
  ProjectStatus,
  UpdateProjectPayload,
  UserOption,
} from '../types';

interface ProjectFormModalProps {
  open: boolean;
  project?: Project | null; // null / undefined = create mode
  users: UserOption[];
  /** User's own department — used to pre-select on create. */
  defaultDepartment?: string;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateProjectPayload | UpdateProjectPayload) => void;
}

type FormState = {
  name: string;
  department: string;
  priority: ProjectPriority;
  status: ProjectStatus;
  deadline: string;
  description: string;
  project_value: string;
  assigned_to: string;
  reference_url: string;
};

const EMPTY: FormState = {
  name: '',
  department: 'it',
  priority: 'medium',
  status: 'active',
  deadline: '',
  description: '',
  project_value: '',
  assigned_to: '',
  reference_url: '',
};

function projectToForm(project: Project): FormState {
  return {
    name: project.name ?? '',
    department: project.department ?? 'it',
    priority: project.priority ?? 'medium',
    status: project.status ?? 'active',
    deadline: project.deadline ?? '',
    description: project.description ?? '',
    project_value: project.project_value != null ? String(project.project_value) : '',
    assigned_to: getAssignedId(project.assigned_to),
    reference_url: project.reference_url ?? '',
  };
}

export function ProjectFormModal({
  open,
  project,
  users,
  defaultDepartment,
  loading,
  onClose,
  onSubmit,
}: ProjectFormModalProps) {
  const isEdit = !!project;
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (project) {
      setForm(projectToForm(project));
    } else {
      setForm({
        ...EMPTY,
        department: defaultDepartment && DEPARTMENT_OPTIONS.some((d) => d.value === defaultDepartment)
          ? defaultDepartment
          : EMPTY.department,
      });
    }
  }, [open, project, defaultDepartment]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    const base = {
      name: form.name.trim(),
      priority: form.priority,
      status: form.status,
      deadline: form.deadline || null,
      description: form.description.trim(),
      project_value: form.project_value ? Number(form.project_value) : 0,
      assigned_to: form.assigned_to || null,
      reference_url: form.reference_url.trim(),
    };

    if (isEdit) {
      // Department is intentionally omitted — immutable server-side.
      onSubmit(base as UpdateProjectPayload);
    } else {
      onSubmit({ ...base, department: form.department } as CreateProjectPayload);
    }
  };

  const assigneeOptions = [
    { value: '', label: '— No Assignment —' },
    ...users.map((u) => ({ value: u.id, label: u.display_name })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Project' : 'New Project'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="project-form" loading={loading}>
            {isEdit ? 'Save Changes' : 'Create Project'}
          </Button>
        </>
      }
    >
      <form id="project-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <Input
          label="Project Name *"
          name="project-name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="Project name…"
          required
        />

        {/* Department + Priority */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Select
              label="Department"
              name="project-department"
              value={form.department}
              onChange={(e) => set('department', e.target.value)}
              options={DEPARTMENT_OPTIONS}
              disabled={isEdit}
            />
            {isEdit && (
              <p className="mt-1 text-body-sm text-on-surface-variant">
                Cannot be changed after creation
              </p>
            )}
          </div>
          <Select
            label="Priority"
            name="project-priority"
            value={form.priority}
            onChange={(e) => set('priority', e.target.value as ProjectPriority)}
            options={PRIORITY_OPTIONS}
          />
        </div>

        {/* Status + Deadline */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Status"
            name="project-status"
            value={form.status}
            onChange={(e) => set('status', e.target.value as ProjectStatus)}
            options={STATUS_OPTIONS}
          />
          <Input
            label="Deadline"
            name="project-deadline"
            type="date"
            value={form.deadline}
            onChange={(e) => set('deadline', e.target.value)}
          />
        </div>

        {/* Project Value + Assigned User */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Project Value (₹)"
            name="project-value"
            type="number"
            min={0}
            step={1000}
            value={form.project_value}
            onChange={(e) => set('project_value', e.target.value)}
            placeholder="0"
          />
          <Select
            label="Assigned User"
            name="project-assigned-to"
            value={form.assigned_to}
            onChange={(e) => set('assigned_to', e.target.value)}
            options={assigneeOptions}
          />
        </div>

        {/* Reference URL */}
        <Input
          label="Reference Website URL"
          name="project-reference-url"
          type="url"
          value={form.reference_url}
          onChange={(e) => set('reference_url', e.target.value)}
          placeholder="https://…"
        />

        {/* Description */}
        <div>
          <label
            htmlFor="project-desc"
            className="mb-1.5 block text-body-sm font-medium text-on-surface"
          >
            Description
          </label>
          <textarea
            id="project-desc"
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Project description…"
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
      </form>
    </Modal>
  );
}
